import React, { useState, useEffect } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";
import { Box, Button, Typography, styled } from "@mui/material";
import { RxCross2 } from "react-icons/rx";
import heic2any from "heic2any";
import { useSelector } from "react-redux";

const CircleContainer = styled("div")({
  width: 150,
  height: 150,
  borderRadius: "50%",
  border: "2px dashed #ccc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  color: "#555",
  fontSize: "14px",
  cursor: "pointer",
  position: "relative",
  backgroundColor: "#f9f9f9",
  overflow: "hidden",
});

const ImagePreview = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "50%",
  position: "absolute",
  top: 0,
  left: 0,
});

const UpdateAdministrationDetail = () => {
  const jwtToken = localStorage.getItem("jwt_token");
  const { search } = useLocation();
  const history = useHistory();

  const paramIdSeprator = (param) => param.split("=")[1];
  const id = paramIdSeprator(search);

  const { organisation_id } = useSelector(
    (state) => state.organization.selectedOrganization
  );

  const [peopleRoleId, setPeopleRoleId] = useState(null);

  const [userDetail, setUserDetail] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    organization_id: localStorage.getItem("organisation_id"),
    employee_id: "",
  });

  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [imageKey, setImageKey] = useState(Date.now());

  const [permissionTypes, setPermissionTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [permissions, setPermissions] = useState([]);

  const addCacheBuster = (url) => {
    if (!url) return url;
    if (url.startsWith("data:")) return url;
    if (url.startsWith("blob:")) return url;
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}t=${imageKey}`;
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  /* IMAGE */

  const handleImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      let finalFile = file;

      if (file.type === "image/heic" || file.type === "image/heif") {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        });

        finalFile = new File(
          [convertedBlob],
          file.name.replace(/\.[^/.]+$/, ".jpeg"),
          { type: "image/jpeg" }
        );
      }

      setSelectedImage(finalFile);
      setImagePreviewUrl(URL.createObjectURL(finalFile));
      setImageKey(Date.now());
    } catch {
      toast.error("Unsupported image format.");
    }
  };

  const handleRemoveImage = (e) => {
    e.preventDefault();
    setSelectedImage(null);
    setImagePreviewUrl("");
    setImageKey(Date.now());
  };

  /* USER INPUT */

  const onChangeHandler = (e) => {
    setUserDetail({ ...userDetail, [e.target.id]: e.target.value });
  };

  /* PERMISSIONS */

  const onChangePermissionType = (type) => {
    setSelectedType(type);

    const mapped = type.permissions_list.map((perm) => ({
      name: perm,
      checked: true,
    }));

    setPermissions(mapped);
  };

  const onCheckboxHandler = (index, value) => {
    const updated = [...permissions];
    updated[index].checked = value;
    setPermissions(updated);
  };

  /* FETCH PERMISSION TYPES */

  const fetchPermissionTypes = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/people-permission`,
      {
        headers: { Authorization: `Bearer ${jwtToken}` },
      }
    );

    const data = await res.json();

    const perms = data.data.map((p) => ({
      ...p,
      value: p.id,
      label: p.type,
    }));

    setPermissionTypes(perms);

    return perms;
  };

  /* FETCH ROLE */

  const fetchRoleDetails = async (people_role_id, permList) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/update-role-data/${people_role_id}/${id}`,
        {
          headers: { Authorization: `Bearer ${jwtToken}` },
        }
      );

      const roleData = await res.json();
      const role = roleData.data;

      const type = permList.find((p) => p.type === role.type);

      if (type) {
        setSelectedType(type);

        const mapped = type.permissions_list.map((perm, index) => ({
          name: perm,
          checked: role.checkbox.split(",").map(Number).includes(index),
        }));

        setPermissions(mapped);
      }
    } catch {
      toast.error("Failed to load role data");
    }
  };

  /* FETCH USER */

  const getAdministrationDetail = async (permList) => {
    try {
      let url = `${process.env.REACT_APP_API_URL}/employee/${id}`;

      if (organisation_id) {
        url += `?org_id=${organisation_id}`;
      }

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      const data = await res.json();

      const {
        first_name,
        last_name,
        email,
        phone_number,
        organization_id,
        employee_id,
        profile_image,
        organisation_and_role,
      } = data.data;

      setUserDetail({
        first_name,
        last_name,
        email,
        phone_number,
        organization_id,
        employee_id,
      });

      if (profile_image) {
        setImagePreviewUrl(profile_image);
        setImageKey(Date.now());
      }

      if (organisation_and_role?.length > 0) {
        const roleId = organisation_and_role[0].people_role_id;
        setPeopleRoleId(roleId);

        fetchRoleDetails(roleId, permList);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const loadInitialData = async () => {
    const permList = await fetchPermissionTypes();
    await getAdministrationDetail(permList);
  };

  /* SUBMIT */

  const onSubmitHandler = async (e) => {
    e.preventDefault();

    let base64Image = null;

    if (selectedImage) {
      base64Image = await handleImageToBase64(selectedImage);
    }

    const body = {
      ...userDetail,
      previous_org_id: localStorage.getItem("organisation_id"),
      organization_id: localStorage.getItem("organisation_id"),
      profile_image: base64Image,
    };

    try {
      /* USER UPDATE */

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/employee/${id}`,
        {
          method: "POST",
          body: JSON.stringify(body),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );

      const result = await res.json();

      if (!res.ok) throw new Error(result.error);

      /* ROLE UPDATE */

      const checkedIndexes = permissions
        .map((p, i) => (p.checked ? i : null))
        .filter((x) => x !== null);

      await fetch(`${process.env.REACT_APP_API_URL}/update-role-data`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: +peopleRoleId,
          people_id: +id,
          organisation_id: organisation_id,
          checkbox: checkedIndexes.join(","),
          type: selectedType?.type,
        }),
      });

      toast.success("User updated successfully");

      setTimeout(() => {
        history.push("/administration");
      }, 500);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <form onSubmit={onSubmitHandler}>
      <h6 className="mb-3">Update Administration Details</h6>

      <Box
        mt={2}
        mb={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Profile Photo:
        </Typography>

        <input
          accept="image/*"
          type="file"
          id="upload-image"
          style={{ display: "none" }}
          onChange={handleImageChange}
        />

        <label htmlFor="upload-image" style={{ position: "relative" }}>
          <CircleContainer>
            {imagePreviewUrl ? (
              <ImagePreview
                key={imageKey}
                src={addCacheBuster(imagePreviewUrl)}
                alt="Profile Preview"
              />
            ) : (
              <span>Upload your profile photo.</span>
            )}
          </CircleContainer>

          {imagePreviewUrl && (
            <Button
              onClick={handleRemoveImage}
              sx={{
                position: "absolute",
                top: "10px",
                right: "-10px",
                backgroundColor: "#f44336",
                color: "#fff",
                borderRadius: "50%",
                width: 28,
                height: 28,
                minWidth: "unset",
              }}
            >
              <RxCross2 size={16} />
            </Button>
          )}
        </label>
      </Box>

      <div className="row">
        {[
          "first_name",
          "last_name",
          "email",
          "phone_number",
          "employee_id",
        ].map((field) => (
          <div className="col-md-6 mt-2" key={field}>
            <label>
              {field.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            </label>

            <input
              type={
                field === "email"
                  ? "email"
                  : field === "phone_number"
                  ? "number"
                  : "text"
              }
              className="form-control"
              id={field}
              value={userDetail[field]}
              onChange={onChangeHandler}
            />
          </div>
        ))}
      </div>

      <h6 className="mt-4">Update Role</h6>

      <div className="col-md-6 mb-3">
        <label>Account Type</label>

        <Select
          options={permissionTypes}
          value={selectedType}
          onChange={onChangePermissionType}
        />
      </div>

      {permissions.length > 0 && (
        <ul className="list-unstyled">
          {permissions.map((perm, index) => (
            <li key={index} className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={perm.checked}
                onChange={(e) =>
                  onCheckboxHandler(index, e.target.checked)
                }
              />

              <label className="form-check-label">
                {perm.name.replace(/_/g, " ")}
              </label>
            </li>
          ))}
        </ul>
      )}

      <button type="submit" className="btn my-3 bg_color">
        Save Changes
      </button>
    </form>
  );
};

export default UpdateAdministrationDetail;